import { useState } from 'react';
import type { MiceProjectView } from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button, IconButton, PopoverMenu } from '@web/ui/core';
import type { MiceImport } from '../hooks/use_mice_import';
import { DeleteProjectDialog, DeleteProjectMenuItem } from './delete_project_button';
import { ImportPreviewDialog } from './import_preview_dialog';

/** O input de arquivo, escondido (o botão e o checklist o abrem), e o diálogo da prévia. */
export function ImportInputs({ importer }: Readonly<{ importer: MiceImport }>) {
  const { fileInput } = importer;
  return (
    <>
      <input
        ref={fileInput}
        type="file"
        accept=".csv,.xlsx"
        className="sr-only"
        aria-label={t('mice.import.file')}
        disabled={importer.uploading}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) importer.analyze(file);
        }}
      />
      <ImportPreviewDialog
        preview={importer.preview}
        busy={importer.uploading}
        onCancel={importer.cancel}
        onConfirm={importer.confirm}
      />
    </>
  );
}

const ITEM =
  'flex w-full items-center rounded-md px-3 py-2 text-left text-meta text-foreground hover:bg-muted';

/** "Resumo", "Importar viajantes" e o menu ⋯ (modelos, exportar e apagar o evento). */
export function ProjectActions({
  importer,
  project,
  canManage,
  onSummary,
}: Readonly<{
  importer: MiceImport;
  project: MiceProjectView | undefined;
  canManage: boolean;
  onSummary: () => void;
}>) {
  const [deleting, setDeleting] = useState(false);
  const items = [
    ...(canManage
      ? [
          { label: t('mice.needs.template'), run: importer.downloadNeedsTemplate },
          { label: t('mice.import.ordersTemplate'), run: importer.downloadOrdersTemplate },
        ]
      : []),
    { label: t('mice.project.export'), run: importer.exportResult },
  ];
  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" onClick={onSummary}>
        {t('mice.project.summary')}
      </Button>
      {canManage ? (
        <Button variant="outline" size="sm" disabled={importer.uploading} onClick={importer.pick}>
          {t('mice.import.open')}
        </Button>
      ) : null}
      <PopoverMenu
        side="bottom"
        align="end"
        width="lg"
        renderTrigger={({ onClick }) => (
          <IconButton
            icon="dots-three"
            variant="outline"
            size="sm"
            aria-label={t('mice.project.moreActions')}
            title={t('mice.project.moreActions')}
            onClick={onClick}
          />
        )}
      >
        {({ close }) => (
          <div className="flex flex-col">
            {items.map((item) => (
              <button
                key={item.label}
                type="button"
                className={ITEM}
                onClick={() => {
                  close();
                  item.run();
                }}
              >
                {item.label}
              </button>
            ))}
            {project && canManage ? (
              <DeleteProjectMenuItem
                project={project}
                className={ITEM}
                onSelect={() => {
                  close();
                  setDeleting(true);
                }}
              />
            ) : null}
          </div>
        )}
      </PopoverMenu>
      {project && canManage ? (
        <DeleteProjectDialog project={project} open={deleting} onOpenChange={setDeleting} />
      ) : null}
    </div>
  );
}
